import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import supportApiService from '../../services/supportApiService';

const SupportCancellationDetails = () => {
  const { id: cancellationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cancellationData, setCancellationData] = useState(null);

  useEffect(() => {
    if (cancellationId) {
      loadCancellationDetails();
    }
  }, [cancellationId]);

  const loadCancellationDetails = async () => {
    try {
      setLoading(true);
      console.log('🔍 Loading cancellation details for ID:', cancellationId);
      const data = await supportApiService.getCancellationDetailsById(cancellationId);
      console.log('📊 Cancellation Details API Response:', data);
      
      if (data.success) {
        setCancellationData(data);
        console.log('✅ Cancellation details loaded successfully');
      } else {
        console.error('❌ Cancellation Details API returned error:', data.message);
        toast.error(data.message || 'Failed to load cancellation details');
      }
    } catch (error) {
      console.error('❌ Cancellation details load error:', error);
      toast.error('Failed to load cancellation details');
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate('/support/dashboard/cancellation-page-details');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    if (!status) return <span className="text-gray-500">-</span>;
    
    const statusLower = status.toLowerCase();
    let badgeClass = 'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium';
    
    switch (statusLower) {
      case 'cancelled':
        badgeClass += ' bg-red-100 text-red-800';
        break;
      default:
        badgeClass += ' bg-gray-100 text-gray-800';
    }
    
    return <span className={badgeClass}>{status}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!cancellationData || !cancellationData.cancellationDetail) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Cancellation Not Found</h2>
          <p className="text-gray-600 mb-4">Unable to load cancellation details.</p>
          <button
            onClick={handleBackClick}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md"
          >
            Back to Cancellations
          </button>
        </div>
      </div>
    );
  }

  const cancellation = cancellationData.cancellationDetail;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Page Header */}
        <div className="mb-6">
          <button
            onClick={handleBackClick}
            className="text-green-600 hover:text-green-800 mb-2 flex items-center"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back to Cancellations
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Cancellation Details</h1>
          <p className="text-gray-600">Customer details for cancellation {cancellation.cancellationId}</p>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Customer Details</h3>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Cancellation ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cancellation ID:
                </label>
                <input
                  type="text"
                  value={cancellation.cancellationId || '-'}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                  readOnly
                />
              </div>

              {/* Booking ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Booking ID:
                </label>
                <input
                  type="text"
                  value={cancellation.bookingId || '-'}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                  readOnly
                />
              </div>

              {/* Hotel Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hotel Name:
                </label>
                <input
                  type="text"
                  value={cancellation.hotelName || '-'}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                  readOnly
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status:
                </label>
                <div className="flex items-center h-10">
                  {getStatusBadge(cancellation.status)}
                </div>
              </div>

              {/* Confirmation Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirmation Number:
                </label>
                <input
                  type="text"
                  value={cancellation.confirmationNo || '-'}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                  readOnly
                />
              </div>

              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name:
                </label>
                <input
                  type="text"
                  value={cancellation.passengerFirstName || '-'}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                  readOnly
                />
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name:
                </label>
                <input
                  type="text"
                  value={cancellation.passengerLastName || '-'}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                  readOnly
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email:
                </label>
                <input
                  type="text"
                  value={cancellation.passengerEmail || '-'}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                  readOnly
                />
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number:
                </label>
                <input
                  type="text"
                  value={cancellation.phoneNumber || '-'}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                  readOnly
                />
              </div>

              {/* Check-in Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Check-in Date:
                </label>
                <input
                  type="text"
                  value={formatDate(cancellation.checkInDate)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                  readOnly
                />
              </div>

              {/* Check-out Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Check-out Date:
                </label>
                <input
                  type="text"
                  value={formatDate(cancellation.checkOutDate)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                  readOnly
                />
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City:
                </label>
                <input
                  type="text"
                  value={cancellation.city || '-'}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                  readOnly
                />
              </div>

              {/* Booking Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Booking Date:
                </label>
                <input
                  type="text"
                  value={formatDate(cancellation.bookingDate)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                  readOnly
                />
              </div>

              {/* Cancellation Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cancellation Date:
                </label>
                <input
                  type="text"
                  value={formatDate(cancellation.cancellationDate)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                  readOnly
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex gap-4">
              <button
                disabled
                className="bg-gray-400 cursor-not-allowed text-white px-6 py-2 rounded-md font-medium"
              >
                Refund
              </button>
              <button
                onClick={handleBackClick}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-md font-medium transition-colors"
              >
                Return to Booking Cancellation
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportCancellationDetails;